import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { addDoc, collection } from 'firebase/firestore';
import { db } from '../Config/Config';
// font-mono italic
export default function Contact({ contactData = {} }) {
  const [formData, setFormData] = useState({
    email: "",
    subject: "",
    message: "",
    name: "",
    mobile: ""
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    const handler = (e) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const disableMotion = prefersReducedMotion || isMobile;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage('');
        setMessageType('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const handlePhoneClick = () => {
    window.open(`tel:${contactData.mobile}`, '_self');
  };

  const handleEmailClick = () => {
    window.open(`mailto:${contactData.email}`, '_self');
  };

  const handleAddressClick = () => {
    // Open in Google Maps
    const encodedAddress = encodeURIComponent(contactData.address);
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, '_blank');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await addDoc(collection(db, "website-message"), {
        ...formData,
        createdAt: new Date(),
        status: "unread",
      });

      setFormData({ email: "", subject: "", message: "", name: "", mobile: "" });
      setMessage('Message sent successfully! We\'ll get back to you soon.');
      setMessageType('success');
    } catch (err) {
      console.error("Error sending message:", err);
      setMessage('Failed to send message. Please try again.');
      setMessageType('error');
    }

    setLoading(false);
  };
  return (
    <div>
      <section id="contact" className="mb-5 ">
        <div className="container two-col ">
          <motion.div
            className="col"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: disableMotion ? 0 : 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl lg:text-5xl font-heading font-bold text-white mb-4">
              <span className="text-secondary">Contact</span> Us
            </h2>

            <p className="text-white">{contactData.title}</p>
            <div className="contact-cards">
              <motion.div
                className="group p-6 bg-white/10 backdrop-blur-md rounded-xl border border-white/20 shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer transform hover:-translate-y-2 hover:scale-105"
                onClick={handlePhoneClick}
                whileHover={disableMotion ? {} : { scale: 1.05, y: -8 }}
                whileTap={disableMotion ? {} : { scale: 0.95 }}
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full text-black group-hover:scale-110 transition-transform duration-300">
                    <svg
                      className="w-5 h-5"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M20 15.5c-1.2 0-2.4-.2-3.6-.6-.3-.1-.7 0-1 .2l-2.2 2.2c-2.8-1.5-5.2-3.8-6.6-6.6l2.2-2.2c.3-.3.4-.7.2-1-.3-1.1-.5-2.3-.5-3.5 0-.6-.4-1-1-1H4c-.6 0-1 .4-1 1 0 9.4 7.6 17 17 17 .6 0 1-.4 1-1v-3.5c0-.6-.4-1-1-1zM19 12h2c0-4.97-4.03-9-9-9v2c3.87 0 7 3.13 7 7zm-4 0h2c0-2.76-2.24-5-5-5v2c1.66 0 3 1.34 3 3z" />
                    </svg>
                  </div>
                  <div className="contact-title">Phone</div>
                </div>
                <div className="text-lg font-semibold group-hover:text-yellow-600 transition-colors duration-300 text-white">
                  {contactData.mobile}
                </div>
                <div className="text-sm text-gray-500 mt-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  Click to call
                </div>
              </motion.div>

              <motion.div
                className="group p-6 bg-white/10 backdrop-blur-md rounded-xl border border-white/20 shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer transform hover:-translate-y-2 hover:scale-105"
                onClick={handleEmailClick}
                whileHover={disableMotion ? {} : { scale: 1.05, y: -8 }}
                whileTap={disableMotion ? {} : { scale: 0.95 }}
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full text-black group-hover:scale-110 transition-transform duration-300">
                    <svg
                      className="w-5 h-5"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                    </svg>
                  </div>
                  <div className="contact-title">Email</div>
                </div>
                <div className="text-lg font-semibold group-hover:text-yellow-600 transition-colors duration-300 text-white">
                  {contactData.email}
                </div>
                <div className="text-sm text-gray-500 mt-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  Click to email
                </div>
              </motion.div>

              <motion.div
                className="group p-6 bg-white/10 backdrop-blur-md rounded-xl border border-white/20 shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer transform hover:-translate-y-2 hover:scale-105"
                onClick={handleAddressClick}
                whileHover={disableMotion ? {} : { scale: 1.05, y: -8 }}
                whileTap={disableMotion ? {} : { scale: 0.95 }}
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full text-black group-hover:scale-110 transition-transform duration-300">
                    <svg
                      className="w-5 h-5"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 010-5 2.5 2.5 0 010 5z" />
                    </svg>
                  </div>
                  <div className="contact-title">Address</div>
                </div>
                <div className="text-lg font-semibold group-hover:text-yellow-600 transition-colors duration-300 text-white">
                  {contactData.address}
                </div>
                <div className="text-sm text-gray-500 mt-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  Click to email
                </div>
              </motion.div>
            </div>
          </motion.div>
          <motion.div
            className="col"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: disableMotion ? 0 : 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <form
              className="form bg-white/10 backdrop-blur-md rounded-xl border border-white/20"
              action="https://formspree.io/f/xqazabcd"
              method="POST"
              onSubmit={handleSubmit}
            >
              <h3 className="text-3xl lg:text-4xl font-extrabold tracking-tight mb-6">
                <span className="bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-600 bg-clip-text text-transparent">
                  Send Us
                </span>{" "}
                <span className="text-[#edf3ff]">a Message</span>
              </h3>
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-white mb-2"
                >
                  Name <span className="text-yellow-400">*</span>
                </label>
                <input
                  name="name"
                  type="text"
                  placeholder="Name"
                  required
                  className="w-full px-4 py-2.5 bg-white border border-white/20 rounded-lg focus:outline-secondary focus:border-secondary transition text-black placeholder-slate-400"
                  value={formData.name}
                  onChange={handleChange}
                />
              </div>
              <div className="mt-4">
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-white mb-2"
                >
                  Email <span className="text-yellow-400">*</span>
                </label>
                <input
                  name="email"
                  type="email"
                  placeholder="Email"
                  required
                  className="w-full px-4 py-2.5 bg-white border border-white/20 rounded-lg focus:outline-secondary focus:border-secondary transition text-black placeholder-slate-400"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
              <div className="mt-4">
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-white mb-2"
                >
                  Contact Number <span className="text-yellow-400">*</span>
                </label>
                <input
                  name="mobile"
                  type="tel"
                  placeholder="Contact Number"
                  required
                  className="w-full px-4 py-2.5 bg-white border border-white/20 rounded-lg focus:outline-secondary focus:border-secondary transition text-black placeholder-slate-400"
                  value={formData.mobile}
                  onChange={handleChange}
                />
              </div>
              <div className="mt-4">
                <label
                  htmlFor="subject"
                  className="block text-sm font-medium text-white "
                >
                  Subject <span className="text-yellow-400">*</span>
                </label>
                <input
                  name="subject"
                  type="text"
                  placeholder="Subject"
                  className="w-full px-4 py-2.5 bg-white border border-white/20 rounded-lg focus:outline-secondary focus:border-secondary transition text-black placeholder-slate-400 mt-2"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="mt-4">
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-white"
                >
                  Message <span className="text-yellow-400">*</span>
                </label>
                <textarea
                  name="message"
                  rows="5"
                  placeholder="Your message..."
                  required
                  className="w-full px-4 py-2.5 bg-white border border-white/20 rounded-lg focus:outline-secondary focus:border-secondary transition text-black placeholder-slate-400 mt-2"
                  value={formData.message}
                  onChange={handleChange}
                ></textarea>
              </div>
              <motion.button
                type="submit"
                className="w-full bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-white font-semibold py-3.5 px-6 rounded-lg transition duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                whileHover={disableMotion ? {} : { scale: 1.02, boxShadow: "0 10px 25px rgba(0,0,0,0.1)" }}
                whileTap={disableMotion ? {} : { scale: 0.98 }}
                disabled={loading}
              >
                {loading ? "Sending..." : "Send Message"}
              </motion.button>

              {/* Message Display */}
              {message && (
                <motion.div
                  className={`mt-4 p-4 rounded-lg border ${messageType === "success"
                    ? "bg-green-50 border-green-200 text-green-800"
                    : "bg-red-50 border-red-200 text-red-800"
                    }`}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: disableMotion ? 0 : 0.3 }}
                >
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0">
                      {messageType === "success" ? (
                        <svg
                          className="w-5 h-5 text-green-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      ) : (
                        <svg
                          className="w-5 h-5 text-red-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{message}</p>
                    </div>
                    <button
                      onClick={() => {
                        setMessage("");
                        setMessageType("");
                      }}
                      className={`flex-shrink-0 ml-2 rounded-full p-1 transition-colors duration-200 ${messageType === "success"
                        ? "hover:bg-green-200 text-green-600"
                        : "hover:bg-red-200 text-red-600"
                        }`}
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                </motion.div>
              )}
            </form>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
